<template>
  <div class="container-fluid mt-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>API Keys</h2>
      <button
        v-if="isSuperAdmin"
        type="button"
        class="btn btn-primary"
        @click="openCreateModal"
      >
        Create API Key
      </button>
    </div>

    <div v-if="!isSuperAdmin" class="alert alert-warning">
      This page requires super admin access.
    </div>

    <div v-if="isSuperAdmin">
      <div v-if="loading" class="alert alert-info">Loading API keys...</div>

      <div v-if="!loading && hasError" class="alert alert-danger" role="alert">
        Failed to load API keys. Please try again.
      </div>

      <div v-if="!loading && !hasError" class="table-responsive">
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role Ceiling</th>
              <th>Account</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Last Used</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="apiKeys.length === 0">
              <td colspan="8" class="text-center">No API keys found.</td>
            </tr>
            <tr v-for="key in apiKeys" :key="key.id">
              <td>{{ key.name }}</td>
              <td>
                <span
                  :class="[
                    'badge',
                    accessCeilingBadgeClass(key.access_ceiling),
                  ]"
                >
                  {{ key.access_ceiling }}
                </span>
              </td>
              <td>{{ key.account_email || "-" }}</td>
              <td>{{ formatDate(key.created_at) }}</td>
              <td>
                {{ key.expires_at ? formatDate(key.expires_at) : "Never" }}
              </td>
              <td>
                {{ key.last_used_at ? formatDate(key.last_used_at) : "Never" }}
              </td>
              <td>
                <span :class="['badge', getStatusBadgeClass(key)]">
                  {{ getStatusText(key) }}
                </span>
              </td>
              <td>
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  :disabled="!key.is_active"
                  @click="revokeKey(key)"
                >
                  Revoke
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create API Key Modal. No backdrop-click close: while the one-time plaintext key is
         shown, an accidental outside click would discard it unrecoverably. -->
    <div
      v-if="showCreateModal"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ plaintextKey ? "API Key Created" : "Create API Key" }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeCreateModal"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="plaintextKey">
              <div class="alert alert-warning">
                <strong>Important:</strong> This key is shown only once. Store
                it in a secret manager now.
              </div>
              <div class="mb-3">
                <label class="form-label">API Key</label>
                <div class="input-group">
                  <input
                    type="text"
                    class="form-control font-monospace"
                    :value="plaintextKey"
                    readonly
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="copyToClipboard"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            <form v-else ref="createFormEl" @submit.prevent="handleCreateKey">
              <div class="mb-3">
                <label for="keyName" class="form-label">Name *</label>
                <input
                  id="keyName"
                  v-model="createForm.name"
                  type="text"
                  class="form-control"
                  required
                  maxlength="128"
                  placeholder="e.g., Production MCP Server"
                />
              </div>
              <div class="mb-3">
                <label for="keyRoleCeiling" class="form-label"
                  >Role Ceiling *</label
                >
                <select
                  id="keyRoleCeiling"
                  v-model="createForm.access_ceiling"
                  class="form-select"
                  required
                >
                  <option
                    v-for="accessCeiling in ACCESS_CEILINGS"
                    :key="accessCeiling"
                    :value="accessCeiling"
                  >
                    {{ accessCeiling }}
                  </option>
                </select>
                <div
                  v-if="createForm.access_ceiling === 'SUPER'"
                  class="form-text text-danger"
                >
                  Warning: a super ceiling grants full API access.
                </div>
                <div v-else class="form-text">
                  The key acts as the account below at this role. A role above
                  that account's live role is rejected — no key is created.
                </div>
              </div>
              <div class="mb-3">
                <label for="keyAccountEmail" class="form-label"
                  >Acts as account</label
                >
                <input
                  id="keyAccountEmail"
                  v-model="createForm.account_email"
                  type="email"
                  class="form-control"
                  placeholder="Leave blank to mint for yourself"
                />
                <div class="form-text">
                  Optional. Enter another account's email to mint a key that
                  acts as that account. A ceiling above that account's live role
                  is rejected, so this grants no access it doesn't already have.
                </div>
              </div>
              <div class="mb-3">
                <label for="keyExpiry" class="form-label">Expiry *</label>
                <input
                  id="keyExpiry"
                  v-model="createForm.expires_at"
                  type="datetime-local"
                  class="form-control"
                  :disabled="createForm.never_expires"
                  :required="!createForm.never_expires"
                />
                <div class="form-check mt-2">
                  <input
                    id="keyNeverExpires"
                    v-model="createForm.never_expires"
                    class="form-check-input"
                    type="checkbox"
                  />
                  <label class="form-check-label" for="keyNeverExpires">
                    Never expires (use sparingly — a perpetual key is a
                    perpetual admin session)
                  </label>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeCreateModal"
            >
              {{ plaintextKey ? "Close" : "Cancel" }}
            </button>
            <button
              v-if="!plaintextKey"
              type="button"
              class="btn btn-primary"
              :disabled="createLoading"
              @click="handleCreateKey"
            >
              <span
                v-if="createLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Create Key
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { useAccount } from "@/composables/account/useAccount";
import { reachesAccessClass } from "@/composables/useAdminCheck";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { apiKeysApi } from "./apiKeysApi";
import { ACCESS_CEILINGS } from "./apiKeysTypes";
import type {
  AccessCeiling,
  ApiKey,
  CreateApiKeyRequest,
} from "./apiKeysTypes";

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
// Not awaited: the page opens on its loading state while the keys arrive.
const { accountData, loaded } = useAccount((account) => {
  if (reachesAccessClass(account, "SUPER")) void fetchApiKeys();
});

const apiKeys = ref<ApiKey[]>([]);
const loading = ref(false);
const hasError = ref(false);
const showCreateModal = ref(false);
const createLoading = ref(false);
const plaintextKey = ref("");
const createFormEl = useTemplateRef<HTMLFormElement>("createFormEl");
const createForm = ref<CreateApiKeyRequest>({
  name: "",
  access_ceiling: "SALES_WRITE",
  expires_at: null,
  never_expires: false,
  account_email: "",
});

// Badge colour tracks how much authority the ceiling carries: super is the whole platform, the
// sales tiers reach internal-admin routes, and a USER/PUBLIC key reaches no more than a client.
const accessCeilingBadgeClass = (accessCeiling: AccessCeiling): string => {
  if (accessCeiling === "SUPER") return "bg-danger";
  if (accessCeiling === "SALES_WRITE" || accessCeiling === "SALES_READ")
    return "bg-info";
  return "bg-secondary";
};

const isSuperAdmin = computed(
  () => loaded.value && reachesAccessClass(accountData, "SUPER")
);

const fetchApiKeys = async () => {
  loading.value = true;
  hasError.value = false;
  try {
    apiKeys.value = await apiKeysApi.list();
  } catch (err: unknown) {
    apiKeys.value = [];
    hasError.value = true;
    globalErrorHandler(err);
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const getStatusText = (key: ApiKey): string => {
  if (key.revoked_at) return "Revoked";
  if (key.expires_at && new Date(key.expires_at) < new Date()) return "Expired";
  return "Active";
};

const getStatusBadgeClass = (key: ApiKey): string => {
  if (key.revoked_at) return "bg-secondary";
  if (key.expires_at && new Date(key.expires_at) < new Date())
    return "bg-warning";
  return "bg-success";
};

const resetCreateForm = () => {
  plaintextKey.value = "";
  createForm.value = {
    name: "",
    access_ceiling: "SALES_WRITE",
    expires_at: null,
    never_expires: false,
    account_email: "",
  };
};

const openCreateModal = () => {
  showCreateModal.value = true;
  resetCreateForm();
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  resetCreateForm();
};

const handleCreateKey = async () => {
  // The Create button sits in the modal footer, outside the <form>, so it can't submit it — native
  // constraint validation (required fields, type="email") never fires on its own. Trigger it here
  // so a malformed "Acts as account" email is caught and shown before it reaches the API.
  if (createFormEl.value && !createFormEl.value.reportValidity()) {
    return;
  }
  if (!createForm.value.name) {
    globalErrorHandler(new Error("Name is required"));
    return;
  }
  if (!createForm.value.never_expires && !createForm.value.expires_at) {
    globalErrorHandler(
      new Error("Set an expiry date, or tick 'Never expires' explicitly.")
    );
    return;
  }

  createLoading.value = true;
  try {
    const payload: CreateApiKeyRequest = {
      name: createForm.value.name,
      access_ceiling: createForm.value.access_ceiling,
      never_expires: createForm.value.never_expires,
      // datetime-local is naive local time; the backend compares expiry against UTC, so
      // convert explicitly or the expiry would shift by the admin's UTC offset.
      expires_at:
        !createForm.value.never_expires && createForm.value.expires_at
          ? new Date(createForm.value.expires_at).toISOString()
          : null,
      // Blank means "mint for myself" — send null so the backend uses the caller's own account.
      account_email: createForm.value.account_email?.trim() || null,
    };

    const response = await apiKeysApi.create(payload);

    plaintextKey.value = response.plaintext_key;
    globalSuccessHandler("Success", "API key created successfully");
    await fetchApiKeys();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    createLoading.value = false;
  }
};

const revokeKey = async (key: ApiKey) => {
  if (!confirm(`Are you sure you want to revoke the API key "${key.name}"?`)) {
    return;
  }

  try {
    await apiKeysApi.revoke(key.id);
    globalSuccessHandler("Success", "API key revoked successfully");
    await fetchApiKeys();
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(plaintextKey.value);
    globalSuccessHandler("Success", "API key copied to clipboard");
  } catch {
    globalErrorHandler(new Error("Failed to copy to clipboard"));
  }
};
</script>

<style lang="scss" scoped>
.table-responsive {
  max-height: 70vh;
}

.modal {
  z-index: 1050;
}
</style>
